import axios from "./axios";

const LOGIN_URL = "/oauth/token";
const SIGNUP_URL = "/users";
const UPDATE_PROFILE_URL = "/users";
const LOGOUT_URL = "/oauth/revoke";
const CURRENT_USER_URL = "/users/me";
const SESSION_CONTEXT_CONFIG = { promangSkipCompanyHeader: true };

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;

// Type definitions for API responses
export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
  error_description?: string;
  errors?: unknown;
}

type AxiosLikeResponse<T> = {
  data?: T;
  status?: number;
};

function normalizeLoginErrorBody(
  body: LoginResponse,
  status?: number,
): LoginResponse {
  const oauthError = typeof body.error === "string" ? body.error.trim() : "";
  const description =
    typeof body.error_description === "string"
      ? body.error_description.trim()
      : "";

  if (
    description &&
    (oauthError === "invalid_grant" || oauthError === "invalid_client")
  ) {
    return { ...body, error: description };
  }

  if (oauthError) {
    return body;
  }
  if (description) {
    return { ...body, error: description };
  }
  if (Array.isArray(body.errors) && body.errors.length > 0) {
    const first = body.errors[0];
    if (typeof first === "string" && first.trim() !== "") {
      return { ...body, error: first.trim() };
    }
  }
  if (status != null && status >= 400) {
    return { ...body, error: "auth.login.server_error" };
  }
  if (!body.access_token) {
    return { ...body, error: "auth.login.token_missing" };
  }
  return body;
}

function parseOAuthTokenResponse(
  response: AxiosLikeResponse<LoginResponse> | undefined,
): LoginResponse {
  if (!response) {
    return { error: "auth.login.network_error" };
  }
  const status = response.status ?? 0;
  const body = (response.data ?? {}) as LoginResponse;
  if (status >= 400) {
    return normalizeLoginErrorBody(body, status);
  }
  return normalizeLoginErrorBody(body, status);
}

export interface UserRoleGrant {
  name: string;
  can: string[];
}

export interface UserData {
  id?: string;
  email?: string;
  roles?: UserRoleGrant[];
  locale?: string;
  createdAt?: string;
  /** Per-project access grants (ProjectAccess.admin) when requested via fields. */
  project_accesses?: Array<{
    project_id?: string | number;
    admin?: boolean;
    role?: string;
  }>;
  company?: {
    id?: string;
    name?: string;
    active_modules?: string[];
    activeTripletexIntegration?: boolean;
    hours_handler_id?: string | number;
  };
}

export interface UserResponse {
  data?: UserData;
  error?: string;
  status?: number;
}

export function bootstrapResponseFailed(
  response: UserResponse | undefined,
): boolean {
  if (!response) {
    return true;
  }
  const status = response.status ?? 0;
  if (status >= 400) {
    return true;
  }
  if (response.error) {
    return true;
  }
  const data = response.data;
  if (data && typeof data === "object" && "error" in data) {
    const message = (data as { error?: unknown }).error;
    if (typeof message === "string" && message.trim() !== "") {
      return true;
    }
  }
  return false;
}

function normalizeBootstrapResponse(
  response: { data: UserData; status?: number },
  fallbackError: string,
): UserResponse {
  const wrapped: UserResponse = {
    data: response.data,
    status: response.status,
  };
  if (bootstrapResponseFailed(wrapped)) {
    return {
      ...wrapped,
      error: fallbackError,
    };
  }
  return wrapped;
}

export interface ApiResponse {
  errors?: unknown;
  error?: string;
  data?: unknown;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

export async function createUserWithEmailAndPassword(
  email: string,
  password: string,
): Promise<ApiResponse> {
  const data = {
    email: email,
    password: password,
    client_id: CLIENT_ID,
  };

  return axios
    .post(SIGNUP_URL, data)
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function loginWithEmailAndPassword(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const data = {
    grant_type: "password",
    scope: "write",
    email: email,
    password: password,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGIN_URL, data, { promangSkipAuthHeader: true })
    .then((response: AxiosLikeResponse<LoginResponse>) => {
      return parseOAuthTokenResponse(response);
    })
    .catch((error: { response?: AxiosLikeResponse<LoginResponse> }) => {
      if (error.response) {
        return parseOAuthTokenResponse(error.response);
      }
      return { error: "auth.login.network_error" } as LoginResponse;
    });
}

export async function updateUserProfile(
  currentPassword: string,
  token: string | undefined,
  email?: string,
  password?: string,
): Promise<ApiResponse> {
  const data = {
    current_password: currentPassword,
    email: email,
    password: password,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  return axios
    .patch(UPDATE_PROFILE_URL, data, config)
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function logoutUserWithToken(token: string): Promise<ApiResponse> {
  const data = {
    token: token,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGOUT_URL, data, { promangSkipAuthHeader: true })
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function requestAccessTokenWithRefreshToken(
  refreshToken: string,
): Promise<LoginResponse> {
  const data = {
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGIN_URL, data, { promangSkipAuthHeader: true })
    .then((response: AxiosLikeResponse<LoginResponse>) => {
      return parseOAuthTokenResponse(response);
    })
    .catch((error: { response?: AxiosLikeResponse<LoginResponse> }) => {
      if (error.response) {
        return parseOAuthTokenResponse(error.response);
      }
      return { error: "auth.login.network_error" } as LoginResponse;
    });
}

export async function getCurrentUser(
  accessToken: string,
): Promise<UserResponse> {
  const config = {
    ...SESSION_CONTEXT_CONFIG,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    // Keep default me payload (`*`) and also load per-project admin grants used by
    // useProjectPermissions / role-view simulation.
    params: {
      fields: "*,project_accesses",
    },
  };

  return await axios
    .get(CURRENT_USER_URL, config)
    .then((response: { data: UserData; status?: number }) => {
      return normalizeBootstrapResponse(response, "Failed to get user");
    })
    .catch((error: { response?: { data?: UserData; status?: number } }) => {
      return normalizeBootstrapResponse(
        {
          data: error.response?.data as UserData,
          status: error.response?.status,
        },
        "Failed to get user",
      );
    });
}

export async function getCurrentUserSettings(
  accessToken: string,
): Promise<UserResponse> {
  const config = {
    ...SESSION_CONTEXT_CONFIG,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await axios
    .get("/users/me/settings", config)
    .then((response: { data: UserData; status?: number }) => {
      return normalizeBootstrapResponse(response, "Failed to get settings");
    })
    .catch(
      (
        error: { response?: { data?: UserData; status?: number } } | unknown,
      ) => {
        if (error && typeof error === "object" && "response" in error) {
          const err = error as {
            response?: { data?: UserData; status?: number };
          };
          return {
            data: err.response?.data,
            error: "Failed to get settings",
            status: err.response?.status,
          } as UserResponse;
        }
        return { error: "Failed to get settings" } as UserResponse;
      },
    );
}
